import {createApi} from '@reduxjs/toolkit/query/react';
import axios from 'axios';
import _ from 'lodash';

import store from '../store';
import {getSharedDocumentData} from '../util/sharedDocumentStorage';
import {getSharedDocumentHeader} from './apiUtils';
import {selectEffectiveOrg} from './session';
import {
  normalizeDocument,
  normalizeDocumentArray,
} from '../util/documentTransform';

const axiosBaseQuery =
  ({baseUrl} = {baseUrl: ''}) =>
  async ({url, method = 'GET', data, params, headers, withCredentials}) => {
    const state = store.getState();
    const {session} = state;
    const effectiveOrg = selectEffectiveOrg(state);
    const _headers = {
      'x-inscope-origin': window.location.origin,
      'x-inscope-org': effectiveOrg?.orgId ?? session.currentOrg?.orgId,
      ...getSharedDocumentHeader(),
      ...headers,
    };
    if (!_.isNil(session.accessToken)) {
      _headers.Authorization = `Bearer ${session.accessToken}`;
    }

    // Include shared document header for document-related API calls
    // Only include if we're making a request to a specific document endpoint
    const sharedDocumentData = getSharedDocumentData();
    if (sharedDocumentData?.documentId) {
      const isDocumentEndpoint = url.startsWith(
        `/document/${sharedDocumentData.documentId}`
      );
      if (isDocumentEndpoint) {
        _headers['x-inscope-shared-document'] = sharedDocumentData.documentId;
      }
    }

    try {
      const result = await axios({
        url: `${baseUrl}${url}`,
        method,
        data,
        params,
        headers: _headers,
        withCredentials: withCredentials || false,
      });
      return {data: result.data};
    } catch (axiosError) {
      const err = axiosError;
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };

export const documentWorkspaceApiSlice = createApi({
  reducerPath: 'documentWorkspaceApi',
  baseQuery: axiosBaseQuery({
    baseUrl: process.env.REACT_APP_DOCUMENT_WORKSPACE_API_URL,
  }),
  tagTypes: ['Documents', 'DocumentSections', 'Folders'],
  endpoints: (builder) => ({
    getDocuments: builder.query({
      query: () => ({
        url: '/documents',
        method: 'GET',
      }),
      // transformResponse: (response) => response.records, // old way
      transformResponse: (response) => {
        const records = response.records;
        return normalizeDocumentArray(records);
      },
      providesTags: (result) =>
        result
          ? _.map(result, ({documentId}) => ({
              type: 'Documents',
              id: documentId,
            }))
          : ['Documents'],
    }),
    getDocumentsByFolderId: builder.query({
      query: ({folderId}) => ({
        url: `/folders/${folderId}/documents`,
        method: 'GET',
      }),
      // transformResponse: (response) => response.records, // old way
      transformResponse: (response) => {
        const records = response.records;
        return normalizeDocumentArray(records);
      },
      providesTags: (result, error, {folderId}) =>
        result
          ? [
              {type: 'FolderDocuments', id: folderId},
              ...result.map(({documentId}) => ({
                type: 'Documents',
                id: documentId,
              })),
            ]
          : [{type: 'FolderDocuments', id: folderId}],
    }),
    getDocument: builder.query({
      query: ({documentId}) => ({
        url: `/document/${documentId}`,
        method: 'GET',
      }),
      transformResponse: (response) => {
        response.record = normalizeDocument(response.record);
        return response;
      },
      providesTags: (result, error, {documentId}) => [
        {type: 'Documents', id: documentId},
      ],
    }),
    getDocumentCollabToken: builder.query({
      query: ({documentId}) => ({
        url: `/document/${documentId}/collabToken`,
        method: 'GET',
      }),
    }),
    createSupportingDocumentsDownloadUrl: builder.mutation({
      query: ({documentId}) => ({
        url: `/document/${documentId}/supportingDocuments`,
        method: 'POST',
      }),
    }),
    createDocument: builder.mutation({
      query: (data) => ({
        url: '/documents',
        method: 'POST',
        data,
      }),
      // transformResponse: (response) => response.record, // old way
      transformResponse: (response) => {
        const record = response.record;
        return normalizeDocument(record);
      },
      invalidatesTags: ['Documents', 'FolderDocuments', 'Folders'],
    }),
    createPdfReviewDocument: builder.mutation({
      queryFn: async (
        {
          file,
          name,
          type,
          status,
          folderId,
          ingestionState,
          fiscalYear,
          documentEndDate,
          presentationFormat,
        },
        _api,
        _extraOptions,
        baseQuery
      ) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('name', name);
        formData.append('type', type);
        formData.append('status', status);
        formData.append('folderId', folderId);
        formData.append('ingestionState', JSON.stringify(ingestionState));
        if (fiscalYear) {
          formData.append('fiscalYear', fiscalYear);
        }
        if (documentEndDate) {
          formData.append('documentEndDate', documentEndDate);
        }
        if (presentationFormat) {
          formData.append('presentationFormat', presentationFormat);
        }

        const result = await baseQuery({
          url: '/documents/pdf-audit',
          method: 'POST',
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (result.error) {
          return {error: result.error};
        }

        return {data: normalizeDocument(result.data.record)};
      },
      invalidatesTags: ['Documents', 'FolderDocuments', 'Folders'],
    }),
    createUploadFileUrl: builder.mutation({
      query: (data) => ({
        url: '/fileUpload',
        method: 'POST',
        data,
      }),
    }),
    deleteDocument: builder.mutation({
      query: ({documentId}) => ({
        url: `/document/${documentId}`,
        method: 'PATCH',
        data: {
          isDeleted: 'true',
        },
      }),
      // transformResponse: (response) => response.record, // old way
      transformResponse: (response) => {
        const record = response.record;
        return normalizeDocument(record);
      },
      invalidatesTags: ['Documents', 'FolderDocuments', 'Folders'],
    }),
    copyDocument: builder.mutation({
      query: ({documentId, name, workbookOption}) => ({
        url: `/document/${documentId}/copy`,
        method: 'POST',
        data: {
          name,
          workbookOption,
        },
      }),
      invalidatesTags: ['Documents', 'FolderDocuments', 'Folders'],
    }),
    markDocumentSaved: builder.mutation({
      query: ({documentId}) => ({
        url: `/document/${documentId}/save`,
        method: 'POST',
      }),
      // transformResponse: (response) => response.record, // old way
      transformResponse: (response) => {
        const record = response.record;
        return normalizeDocument(record);
      },
      invalidatesTags: (result, error, arg) => [
        {type: 'Documents', id: arg.document},
      ],
    }),
    updateDocument: builder.mutation({
      query: ({documentId, data}) => ({
        url: `/document/${documentId}`,
        method: 'PATCH',
        data,
      }),
      // transformResponse: (response) => response.record, // old way
      transformResponse: (response) => {
        const record = response.record;
        return normalizeDocument(record);
      },
      invalidatesTags: (result, error, arg) => {
        const tags = [{type: 'Documents', id: arg.documentId}];
        if (arg.data && arg.data.folderId) {
          tags.push('FolderDocuments');
          tags.push('Folders');
        }
        return tags;
      },
    }),
    createDocumentNamedItem: builder.mutation({
      query: ({documentId, data}) => ({
        url: `/document/${documentId}/namedItems`,
        method: 'POST',
        data,
      }),
      transformResponse: (response) => response.record,
    }),
    createDocumentSection: builder.mutation({
      query: ({documentId, data}) => ({
        url: `/document/${documentId}/sections`,
        method: 'POST',
        data,
      }),
      transformResponse: (response) => response.record,
      invalidatesTags: (result, error, {documentId}) => [
        {type: 'Documents', id: documentId},
        'DocumentSections',
      ],
    }),
    updateDocumentSection: builder.mutation({
      query: ({documentId, sectionId, data}) => ({
        url: `/document/${documentId}/sections/${sectionId}`,
        method: 'PATCH',
        data,
      }),
      transformResponse: (response) => response.record,
      invalidatesTags: (result, error, {documentId, sectionId}) => [
        {type: 'Documents', id: documentId},
        {type: 'DocumentSections', id: sectionId},
      ],
    }),
    deleteDocumentSection: builder.mutation({
      query: ({documentId, sectionId}) => ({
        url: `/document/${documentId}/sections/${sectionId}`,
        method: 'DELETE',
      }),
      transformResponse: (response) => response.record,
      invalidatesTags: (result, error, {documentId, sectionId}) => [
        {type: 'Documents', id: documentId},
        {type: 'DocumentSections', id: sectionId},
      ],
    }),
    getSectionTemplates: builder.query({
      query: () => ({
        url: `/templates`,
        method: 'GET',
      }),
      transformResponse: (response) => response.records,
    }),
    getSectionTemplateUpdates: builder.query({
      query: ({documentId}) => ({
        url: `/document/${documentId}/sections/template-updates`,
        method: 'GET',
      }),
      transformResponse: (response) => response.updates,
    }),
    syncSectionTemplate: builder.mutation({
      query: ({documentId, sectionId}) => ({
        url: `/document/${documentId}/sections/${sectionId}/sync-template`,
        method: 'POST',
      }),
    }),
    getHeaderPresets: builder.query({
      query: ({includeConfig = false} = {}) => {
        const params = new URLSearchParams();

        params.append('includeConfig', includeConfig);

        const queryString = params.toString() ? `?${params.toString()}` : '';
        return {
          url: `/header-presets${queryString}`,
          method: 'GET',
        };
      },
      transformResponse: (response) => response.records,
    }),
    getHeaderPresetUpdates: builder.query({
      query: ({documentId}) => ({
        url: `/document/${documentId}/header-updates`,
        method: 'GET',
      }),
      transformResponse: (response) => response.updates,
    }),
    syncHeaderPreset: builder.mutation({
      query: ({documentId, sectionId, presetId, startPage}) => ({
        url: `/document/${documentId}/sync-header`,
        method: 'POST',
        data: {
          sectionId,
          presetId,
          ...(startPage !== undefined && {startPage}),
        },
      }),
    }),
    getFooterPresets: builder.query({
      query: ({includeConfig = false} = {}) => {
        const params = new URLSearchParams();

        params.append('includeConfig', includeConfig);

        const queryString = params.toString() ? `?${params.toString()}` : '';
        return {
          url: `/footer-presets${queryString}`,
          method: 'GET',
        };
      },
      transformResponse: (response) => response.records,
    }),
    getFooterPresetUpdates: builder.query({
      query: ({documentId}) => ({
        url: `/document/${documentId}/footer-updates`,
        method: 'GET',
      }),
      transformResponse: (response) => response.updates,
    }),
    syncFooterPreset: builder.mutation({
      query: ({documentId, sectionId, presetId, startPage}) => ({
        url: `/document/${documentId}/sync-footer`,
        method: 'POST',
        data: {
          sectionId,
          presetId,
          ...(startPage !== undefined && {startPage}),
        },
      }),
    }),
    createFolder: builder.mutation({
      query: ({data}) => ({
        url: `/folders`,
        method: 'POST',
        data,
      }),
      transformResponse: (response) => response.record || response,
      invalidatesTags: ['Folders'],
      async onQueryStarted({data}, {dispatch, queryFulfilled}) {
        const {updateQueryData} = documentWorkspaceApiSlice.util;

        // Optimistically update the cache
        const patchResult = dispatch(
          updateQueryData('getFolders', undefined, (draft) => {
            const tempFolder = {
              id: `temp-${Date.now()}`,
              name: data.name,
              year: data.year,
              documentCount: 0,
              createdAt: new Date().toISOString(),
            };

            draft.push(tempFolder);
          })
        );

        try {
          const {data: createdFolder} = await queryFulfilled;

          dispatch(
            updateQueryData('getFolders', undefined, (draft) => {
              const index = draft.findIndex(
                (f) =>
                  f.name === data.name &&
                  f.year === data.year &&
                  f.id.startsWith('temp-')
              );
              if (index !== -1) {
                draft[index] = createdFolder;
              }
            })
          );
        } catch {
          patchResult.undo();
        }
      },
    }),
    getFolders: builder.query({
      query: () => ({
        url: `/folders`,
        method: 'GET',
      }),
      transformResponse: (response) => response.records,
      providesTags: ['Folders'],
    }),
    getFolder: builder.query({
      query: ({folderId}) => ({
        url: `/folders/${folderId}`,
        method: 'GET',
      }),
      transformResponse: (response) => response.record || response,
      providesTags: (result, error, {folderId}) => [
        {type: 'Folders', id: folderId},
      ],
    }),
    renameFolder: builder.mutation({
      query: ({folderId, data}) => ({
        url: `/folders/${folderId}/rename`,
        method: 'PATCH',
        data,
      }),
      transformResponse: (response) => response.record || response,
      invalidatesTags: (result, error, arg) => [
        'Folders',
        {type: 'Folders', id: arg.folderId},
      ],
    }),
    moveFolder: builder.mutation({
      query: ({folderId, data}) => ({
        url: `/folders/${folderId}/move`,
        method: 'PATCH',
        data,
      }),
      transformResponse: (response) => response.record || response,
      invalidatesTags: (result, error, arg) => [
        'Folders',
        {type: 'Folders', id: arg.folderId},
      ],
    }),
    deleteFolder: builder.mutation({
      query: ({folderId}) => ({
        url: `/folders/${folderId}`,
        method: 'DELETE',
      }),
      transformResponse: (response) => response.record || response,
      invalidatesTags: ['Folders', 'FolderDocuments', 'Documents'],
    }),
    getShareSettings: builder.query({
      query: ({documentId}) => ({
        url: `/document/${documentId}/shareSettings`,
        method: 'GET',
      }),
      transformResponse: (response) => response.record || response,
    }),
    getDocumentsSharedWithMe: builder.query({
      query: () => ({
        url: `/documents/sharedWithMe`,
        method: 'GET',
      }),
    }),
    createShareLink: builder.mutation({
      query: ({
        documentId,
        orgId,
        visibility,
        grantedPermission,
        requiredOrgRoles,
        parentOrgId,
        parentOrgRequiredRoles,
        parentOrgRequiredRolePermissionMapping,
      }) => {
        const data = {orgId};
        if (visibility) data.visibility = visibility;
        if (grantedPermission) data.grantedPermission = grantedPermission;
        if (requiredOrgRoles) data.requiredOrgRoles = requiredOrgRoles;
        if (parentOrgId) data.parentOrgId = parentOrgId;
        if (parentOrgRequiredRoles)
          data.parentOrgRequiredRoles = parentOrgRequiredRoles;
        if (parentOrgRequiredRolePermissionMapping)
          data.parentOrgRequiredRolePermissionMapping =
            parentOrgRequiredRolePermissionMapping;
        return {
          url: `/document/${documentId}/shareSettings`,
          method: 'POST',
          data,
        };
      },
      transformResponse: (response) => response.record || response,
    }),
    regenerateShareLink: builder.mutation({
      query: ({documentId, emails}) => {
        if (!emails || !Array.isArray(emails) || emails.length === 0) {
          throw new Error('Emails array must not be empty');
        }
        return {
          url: `/document/${documentId}/shareSettings/relink`,
          method: 'POST',
          data: {emails},
        };
      },
    }),
    updateShareLink: builder.mutation({
      query: ({documentId, data}) => ({
        url: `/document/${documentId}/shareSettings`,
        method: 'PATCH',
        data,
      }),
    }),
    validateShareLink: builder.query({
      query: ({documentId}) => ({
        url: `/document/${documentId}/shareSettings/validate`,
        method: 'GET',
      }),
      transformResponse: (response) => response.record || response,
    }),
    getDocumentStatusHistory: builder.query({
      query: ({documentId}) => ({
        url: `/document/${documentId}/statusHistory`,
        method: 'GET',
      }),
    }),
    getImageCookies: builder.query({
      query: () => ({
        url: '/auth/cloudfront-cookies',
        method: 'POST',
        withCredentials: true,
      }),
    }),
    clearAuditTrail: builder.mutation({
      query: ({documentId}) => ({
        url: `/document/${documentId}/lock`,
        method: 'POST',
      }),
      transformResponse: (response) => response.record || response,
    }),
    cancelClearAuditTrail: builder.mutation({
      query: ({documentId}) => ({
        url: `/document/${documentId}/cancel-lock`,
        method: 'POST',
      }),
      transformResponse: (response) => response.record || response,
    }),
    getPDFAuthToken: builder.query({
      query: ({documentId}) => ({
        url: `/document/${documentId}/pdf-auth-token`,
        method: 'GET',
      }),
      // providesTags: (result, error, {documentId}) => [
      //   {type: 'PDFAuthToken', id: documentId},
      // ],
      // // Token expires in 1 hour, refetch after 50 minutes
      // keepUnusedDataFor: 50 * 60,
    }),
  }),
});

export const {
  endpoints,
  useCreateDocumentMutation,
  useCreatePdfReviewDocumentMutation,
  useCreateSupportingDocumentsDownloadUrlMutation,
  useCreateDocumentNamedItemMutation,
  useCreateDocumentSectionMutation,
  useCreateUploadFileUrlMutation,
  useCopyDocumentMutation,
  useDeleteDocumentMutation,
  useDeleteDocumentSectionMutation,
  useGetDocumentQuery,
  useGetDocumentCollabTokenQuery,
  useGetDocumentsQuery,
  useGetDocumentsByFolderIdQuery,
  useGetDocumentsSharedWithMeQuery,
  useGetSectionTemplateUpdatesQuery,
  useMarkDocumentSavedMutation,
  useUpdateDocumentMutation,
  useUpdateDocumentSectionMutation,
  useCreateFolderMutation,
  useGetFoldersQuery,
  useGetFolderQuery,
  useDeleteFolderMutation,
  useRenameFolderMutation,
  useMoveFolderMutation,
  useCreateShareLinkMutation,
  useGetShareSettingsQuery,
  useUpdateShareLinkMutation,
  useRegenerateShareLinkMutation,
  useValidateShareLinkQuery,
  useGetDocumentStatusHistoryQuery,
  useGetImageCookiesQuery,
  useClearAuditTrailMutation,
  useCancelClearAuditTrailMutation,
  useGetSectionTemplatesQuery,
  useSyncSectionTemplateMutation,
  useGetHeaderPresetsQuery,
  useGetHeaderPresetUpdatesQuery,
  useSyncHeaderPresetMutation,
  useGetFooterPresetsQuery,
  useGetFooterPresetUpdatesQuery,
  useSyncFooterPresetMutation,
  useLazyGetPDFAuthTokenQuery,
} = documentWorkspaceApiSlice;
